import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  Chip,
  Grid,
  Stack,
} from "@mui/material";
import ExploreIcon from "@mui/icons-material/Explore";
import TuneIcon from "@mui/icons-material/Tune";
import WidgetsIcon from "@mui/icons-material/Widgets";
import VerifiedIcon from "@mui/icons-material/Verified";
import PublicIcon from "@mui/icons-material/Public";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const BRAND_RED = "#E50000";
const DARK_BG = "#0F172A";

const TourCardMock = ({ title, price, category }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      background: "rgba(255,255,255,0.06)",
      backdropFilter: "blur(12px)",
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: 2,
      flex: 1,
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        height: 72,
        borderRadius: 1.5,
        mb: 1.5,
        background:
          "linear-gradient(135deg, rgba(229,0,0,0.25) 0%, rgba(15,23,42,0.5) 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <PublicIcon sx={{ color: "rgba(255,255,255,0.4)", fontSize: 28 }} />
    </Box>
    <Typography variant="body2" fontWeight={600} color="white" noWrap mb={0.5}>
      {title}
    </Typography>
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)" }}>
        {category}
      </Typography>
      <Typography variant="body2" fontWeight={700} sx={{ color: BRAND_RED }}>
        ${price}
      </Typography>
    </Box>
  </Paper>
);

const PricingMock = () => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      background: "rgba(229,0,0,0.1)",
      backdropFilter: "blur(12px)",
      border: "1px solid rgba(229,0,0,0.22)",
      borderRadius: 2,
    }}
  >
    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
      <TuneIcon sx={{ color: BRAND_RED, fontSize: 16 }} />
      <Typography
        variant="caption"
        fontWeight={700}
        color="white"
        letterSpacing="0.04em"
      >
        DISTRIBUTOR PRICING RULE
      </Typography>
    </Box>
    {[
      { label: "Base Price", val: "$250" },
      { label: "Markup", val: "+15%" },
      { label: "Your Price", val: "$287.50", highlight: true },
    ].map(({ label, val, highlight }) => (
      <Box key={label} display="flex" justifyContent="space-between" mb={0.5}>
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)" }}>
          {label}
        </Typography>
        <Typography
          variant="caption"
          fontWeight={highlight ? 700 : 500}
          sx={{ color: highlight ? "#FF6B6B" : "rgba(255,255,255,0.75)" }}
        >
          {val}
        </Typography>
      </Box>
    ))}
  </Paper>
);

const STEPS = [
  {
    icon: <ExploreIcon />,
    title: "Tour Owners List Packages",
    desc: "Create rich tour packages with itineraries, pricing tiers, availability slots, and media galleries, all in one place.",
  },
  {
    icon: <TuneIcon />,
    title: "Distributors Set Pricing",
    desc: "Apply markup or markdown rules per tour. Each distributor controls their own margin independently.",
  },
  {
    icon: <WidgetsIcon />,
    title: "Embed & Sell Anywhere",
    desc: "Paste one script tag and a fully featured booking widget appears on any website, instantly.",
  },
];

const OWNER_FEATURES = [
  "Create unlimited tour packages",
  "Upload photos, itineraries & availability",
  "Track bookings & revenue in real time",
  "Manage multiple distributor partners",
];

const DIST_FEATURES = [
  "Access an aggregated tour catalog",
  "Set custom pricing rules per package",
  "Embed the booking widget on any site",
  "Independent earnings dashboard",
];

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <Box>
      {/* ─── Navbar ─── */}
      <Box
        component="nav"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          bgcolor: "rgba(15,23,42,0.92)",
          backdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          px: { xs: 2.5, md: 6 },
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <img src="/logo.png" alt="Sabre" style={{ height: 34 }} />
        <Box display="flex" gap={1.5} alignItems="center">
          <Button
            onClick={() => navigate("/login")}
            sx={{
              color: "rgba(255,255,255,0.75)",
              "&:hover": { color: "white", bgcolor: "rgba(255,255,255,0.06)" },
            }}
          >
            Sign In
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate("/signup")}
            sx={{ px: 2.5 }}
          >
            Get Started
          </Button>
        </Box>
      </Box>

      {/* ─── Hero ─── */}
      <Box
        sx={{
          bgcolor: DARK_BG,
          minHeight: "88vh",
          display: "flex",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(rgba(229,0,0,0.07) 1px, transparent 1px)",
            backgroundSize: "30px 30px",
            pointerEvents: "none",
          },
        }}
      >
        {/* Glow orb */}
        <Box
          sx={{
            position: "absolute",
            top: "5%",
            left: "40%",
            width: 700,
            height: 700,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(229,0,0,0.12) 0%, transparent 60%)",
            pointerEvents: "none",
          }}
        />

        <Container
          maxWidth="lg"
          sx={{ position: "relative", zIndex: 1, py: { xs: 8, md: 10 } }}
        >
          <Grid container spacing={6} alignItems="center">
            {/* Left — copy */}
            <Grid item xs={12} md={6}>
              <Chip
                label="B2B Tour Distribution Platform"
                size="small"
                sx={{
                  bgcolor: "rgba(229,0,0,0.14)",
                  color: "#FF8080",
                  fontWeight: 600,
                  mb: 3.5,
                  border: "1px solid rgba(229,0,0,0.28)",
                  letterSpacing: "0.02em",
                }}
              />
              <Typography
                component="h1"
                fontWeight={800}
                color="white"
                sx={{
                  fontSize: { xs: "2.5rem", sm: "3rem", md: "3.5rem" },
                  lineHeight: 1.08,
                  letterSpacing: "-0.025em",
                  mb: 3,
                }}
              >
                Sell tours.{" "}
                <Box component="span" sx={{ color: BRAND_RED }}>
                  Everywhere.
                </Box>
              </Typography>
              <Typography
                variant="h6"
                fontWeight={400}
                sx={{
                  color: "rgba(255,255,255,0.55)",
                  mb: 4.5,
                  maxWidth: 480,
                  lineHeight: 1.7,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                }}
              >
                Sabre connects tour owners with distributors. List packages,
                apply custom pricing rules, and let partners sell through an
                embeddable widget — all from one platform.
              </Typography>

              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate("/signup")}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ px: 3.5, py: 1.45, fontSize: "1rem" }}
                >
                  Start for free
                </Button>
                <Button
                  size="large"
                  onClick={() => navigate("/login")}
                  sx={{
                    px: 3.5,
                    py: 1.45,
                    fontSize: "1rem",
                    color: "rgba(255,255,255,0.7)",
                    border: "1px solid rgba(255,255,255,0.15)",
                    borderRadius: "8px",
                    "&:hover": {
                      color: "white",
                      border: "1px solid rgba(255,255,255,0.35)",
                      bgcolor: "rgba(255,255,255,0.05)",
                    },
                  }}
                >
                  Sign in
                </Button>
              </Box>

              <Box display="flex" gap={5} mt={5.5} flexWrap="wrap">
                {[
                  { val: "Multi-role", sub: "Owner & Distributor" },
                  { val: "Real-time", sub: "Bookings & Pricing" },
                  { val: "1 Script", sub: "Widget Embed" },
                ].map(({ val, sub }) => (
                  <Box key={sub}>
                    <Typography
                      variant="h6"
                      fontWeight={800}
                      color="white"
                      lineHeight={1.1}
                    >
                      {val}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(255,255,255,0.4)" }}
                    >
                      {sub}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Right — product mock */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  pl: { md: 4 },
                }}
              >
                <Box display="flex" gap={2}>
                  <TourCardMock
                    title="Zanzibar Beach Safari"
                    price="340"
                    category="Adventure"
                  />
                  <TourCardMock
                    title="Serengeti Sunrise"
                    price="510"
                    category="Wildlife"
                  />
                </Box>
                <PricingMock />
                {/* Embed snippet */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    background: "rgba(8,14,26,0.85)",
                    border: "1px solid rgba(255,255,255,0.07)",
                    borderRadius: 2,
                    fontFamily: "'Fira Code', 'Courier New', monospace",
                    fontSize: "0.78rem",
                    lineHeight: 1.9,
                    overflowX: "auto",
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      color: "#94A3B8",
                      fontSize: "inherit",
                      fontFamily: "inherit",
                    }}
                  >
                    {"<script "}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      color: "#86EFAC",
                      fontSize: "inherit",
                      fontFamily: "inherit",
                    }}
                  >
                    {"src"}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      color: "#94A3B8",
                      fontSize: "inherit",
                      fontFamily: "inherit",
                    }}
                  >
                    {"="}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      color: "#FCA5A5",
                      fontSize: "inherit",
                      fontFamily: "inherit",
                    }}
                  >
                    {'"https://widget.sabretours.io/embed.js"'}
                  </Typography>
                  <br />
                  <Typography
                    component="span"
                    sx={{
                      color: "#86EFAC",
                      fontSize: "inherit",
                      fontFamily: "inherit",
                      ml: "1ch",
                    }}
                  >
                    {"  data-distributor-id"}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      color: "#94A3B8",
                      fontSize: "inherit",
                      fontFamily: "inherit",
                    }}
                  >
                    {"="}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      color: "#FCA5A5",
                      fontSize: "inherit",
                      fontFamily: "inherit",
                    }}
                  >
                    {'"your-id"'}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      color: "#94A3B8",
                      fontSize: "inherit",
                      fontFamily: "inherit",
                    }}
                  >
                    {"></script>"}
                  </Typography>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ─── How It Works ─── */}
      <Box sx={{ bgcolor: "#F7F8FC", py: { xs: 9, md: 13 } }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={9}>
            <Typography
              variant="overline"
              sx={{
                color: BRAND_RED,
                fontWeight: 700,
                letterSpacing: "0.12em",
              }}
            >
              How It Works
            </Typography>
            <Typography
              variant="h4"
              fontWeight={800}
              color="text.primary"
              mt={1}
            >
              Three steps to distribution
            </Typography>
          </Box>

          <Grid container spacing={4} position="relative">
            {STEPS.map(({ icon, title, desc }, i) => (
              <Grid item xs={12} md={4} key={title}>
                <Box textAlign="center" px={2}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: "50%",
                      bgcolor: "rgba(229,0,0,0.09)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2,
                      color: BRAND_RED,
                      fontSize: 26,
                      border: "1px solid rgba(229,0,0,0.15)",
                    }}
                  >
                    {icon}
                  </Box>
                  <Chip
                    label={`Step ${i + 1}`}
                    size="small"
                    sx={{
                      bgcolor: "rgba(229,0,0,0.08)",
                      color: BRAND_RED,
                      fontWeight: 600,
                      mb: 2,
                      fontSize: "0.72rem",
                    }}
                  />
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    color="text.primary"
                    mb={1.5}
                  >
                    {title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    lineHeight={1.8}
                  >
                    {desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ─── Role cards ─── */}
      <Box sx={{ bgcolor: "white", py: { xs: 9, md: 13 } }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={9}>
            <Typography
              variant="overline"
              sx={{
                color: BRAND_RED,
                fontWeight: 700,
                letterSpacing: "0.12em",
              }}
            >
              Built for both sides
            </Typography>
            <Typography
              variant="h4"
              fontWeight={800}
              color="text.primary"
              mt={1}
            >
              One platform, two roles
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {/* Tour Owner */}
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: { xs: 3.5, md: 5 },
                  height: "100%",
                  border: "1px solid #F0F0F5",
                  background:
                    "linear-gradient(145deg, #ffffff 0%, #FFF5F5 100%)",
                  boxShadow: "0 4px 32px rgba(229,0,0,0.06)",
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 2,
                    bgcolor: "rgba(229,0,0,0.09)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 3,
                    color: BRAND_RED,
                  }}
                >
                  <ExploreIcon fontSize="medium" />
                </Box>
                <Chip
                  label="For Tour Owners"
                  size="small"
                  sx={{
                    bgcolor: "rgba(229,0,0,0.09)",
                    color: BRAND_RED,
                    fontWeight: 600,
                    mb: 2.5,
                  }}
                />
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color="text.primary"
                  mb={1.5}
                >
                  List your tours, reach more customers
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  mb={3.5}
                  lineHeight={1.8}
                >
                  Create and manage your entire tour catalog. Let distributors
                  handle the selling — you focus on delivering amazing
                  experiences.
                </Typography>
                <Stack spacing={1.5}>
                  {OWNER_FEATURES.map((f) => (
                    <Box key={f} display="flex" alignItems="center" gap={1.5}>
                      <CheckCircleOutlineIcon
                        sx={{ color: BRAND_RED, fontSize: 18, flexShrink: 0 }}
                      />
                      <Typography variant="body2" color="text.primary">
                        {f}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
                <Button
                  variant="contained"
                  sx={{ mt: 4.5 }}
                  onClick={() => navigate("/signup")}
                >
                  Register as Owner
                </Button>
              </Paper>
            </Grid>

            {/* Distributor */}
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: { xs: 3.5, md: 5 },
                  height: "100%",
                  bgcolor: DARK_BG,
                  border: "1px solid rgba(229,0,0,0.18)",
                  boxShadow: "0 4px 32px rgba(0,0,0,0.25)",
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 2,
                    bgcolor: "rgba(229,0,0,0.14)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 3,
                    color: "#FF8080",
                  }}
                >
                  <WidgetsIcon fontSize="medium" />
                </Box>
                <Chip
                  label="For Distributors"
                  size="small"
                  sx={{
                    bgcolor: "rgba(229,0,0,0.14)",
                    color: "#FF8080",
                    fontWeight: 600,
                    mb: 2.5,
                    border: "1px solid rgba(229,0,0,0.28)",
                  }}
                />
                <Typography
                  variant="h5"
                  fontWeight={700}
                  color="white"
                  mb={1.5}
                >
                  Sell tours on your own site
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.5)",
                    mb: 3.5,
                    lineHeight: 1.8,
                  }}
                >
                  Browse the aggregated catalog, set your own margins, and embed
                  a seamless booking widget on your website in minutes.
                </Typography>
                <Stack spacing={1.5}>
                  {DIST_FEATURES.map((f) => (
                    <Box key={f} display="flex" alignItems="center" gap={1.5}>
                      <CheckCircleOutlineIcon
                        sx={{ color: "#FF8080", fontSize: 18, flexShrink: 0 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(255,255,255,0.78)" }}
                      >
                        {f}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
                <Button
                  variant="outlined"
                  sx={{
                    mt: 4.5,
                    color: "white",
                    borderColor: "rgba(229,0,0,0.35)",
                    "&:hover": {
                      borderColor: BRAND_RED,
                      bgcolor: "rgba(229,0,0,0.08)",
                    },
                  }}
                  onClick={() => navigate("/signup")}
                >
                  Register as Distributor
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ─── CTA Banner ─── */}
      <Box
        sx={{
          bgcolor: DARK_BG,
          py: { xs: 9, md: 12 },
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(rgba(229,0,0,0.06) 1px, transparent 1px)",
            backgroundSize: "30px 30px",
            pointerEvents: "none",
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(229,0,0,0.1) 0%, transparent 60%)",
            pointerEvents: "none",
          }}
        />
        <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
          <VerifiedIcon sx={{ color: BRAND_RED, fontSize: 44, mb: 2 }} />
          <Typography variant="h4" fontWeight={800} color="white" mb={2}>
            Ready to grow your tour business?
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "rgba(255,255,255,0.5)", mb: 4.5, lineHeight: 1.7 }}
          >
            Join tour owners and distributors already using Sabre to reach wider
            audiences and drive more bookings.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/signup")}
            endIcon={<ArrowForwardIcon />}
            sx={{ px: 4, py: 1.5, fontSize: "1rem" }}
          >
            Create your free account
          </Button>
        </Container>
      </Box>

      {/* ─── Footer ─── */}
      <Box
        component="footer"
        sx={{
          bgcolor: "#080E1A",
          py: 2.5,
          px: { xs: 3, md: 6 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <img
          src="/logo.png"
          alt="Sabre"
          style={{ height: 26, opacity: 0.55 }}
        />
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.25)" }}>
          © {new Date().getFullYear()} Sabre Tour Aggregator. All rights
          reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default LandingPage;
